import React, { Component } from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

import JsonTextEditor from '../common/editors/JsonTextEditor';
import IntentionsSelector from './IntentionsSelector';
import TypeSelector from './TypeSelector';
import { IconButton } from '../common/IconButton';
import ArticleEditor from './ArticleEditor';
import SymptomLegoEditor from './SymptomLegoEditor';
import TextFieldEditor from '../common/editors/TextFieldEditor';
import SourceEditor from './SourceEditor';
import { LegoStateSwitch } from './LegoStateSwitch';
import ModalManager from '../ModalManager';
import EventEmitter from 'events';
import MultipleContextsEditor from './MultipleContextsEditor';
import FuseboxLegoEditor from './fusebox-editor/FuseboxLegoEditor';
import { SingleImageUrlEditor } from '../common/images/SingleImageUrlEditor';
import JsonListEditor from '../common/editors/JsonListEditor';
import JsonGUIEditor from "../common/editors/JsonGUIEditor";
import LeftRightLayout from '../common/layout/LeftRightLayout';
import InputMultipleSemantics from '../common/editors/InputMultipleSemantics';
import LegoLabelsSelector from './LegoLabelsSelector';
import BadgeId from '../common/BadgeId';
import LegoContextSummary from './LegoContextSummary';
import SingleTextEditor from '../common/editors/SingleTextEditor';
import WarningLightsSymptomLegoEditor from './WarningLightsSymptomLegoEditor';
import { ImagePreview } from '../images/ImagePreview';
import NotesEditor from '../common/editors/NotesEditor';
import { LocalesSelector } from './LocalesSelector';
import { LangTranslationSelector } from './LangTranslationSelector';
import LegoAdminPageContext from '../../pages/legoAdminPageContext';
import ArticleMarkupRichTextEditor from './article-editor/ArticleMarkupRichTextEditor';
import { UserAvatarLive } from '../common/UserAvatarLive';
import BtnSeeDiff from '../common/editors/BtnSeeDiff';
import LegoEditButton from './LegoEditButton';
import NiceDate from '../common/NiceDate';
import PublishedFuseboxThumbnail from './fusebox-editor/PublishedFuseboxThumbnail';

class LegoEditor extends Component {
  static contextType = LegoAdminPageContext;

  constructor(props) {
    super(props);

    this.modalActionsBus = new EventEmitter();

    this.state = {
      original: this.props.lego,
      changedObject: _.cloneDeep(this.props.lego),
    };

    this.dataEditorRef = React.createRef();

    this.legoForceUpdateEventService = window.client.service('/services/legos-force-update-event');
    this.legoTemplateRenameService = window.client.service('/services/legos/rename-fusebox-template');
  }

  componentDidMount() {
    if(this.state.changedObject.context && !_.isArray(this.state.changedObject.context)) {
      this.editField('context', [this.state.changedObject.context]);
    }
  }

  async defaultSave(changedLego, closeDialog) {
    if (changedLego._id) {
      changedLego.updatedBy = this.context.page.getLoggedUserSignature();
      const res = await this.context.page.service('/services/legos').update(changedLego._id, changedLego);
      if (closeDialog) {
        this.context.page.modalActionsBus.emit('close');
      }
      if(this.props.onAfterSave) {
        this.props.onAfterSave(res);
      }
      return res;
    } else {
      alert('Default LegoEditor cannot create legos without an onSave prop handling it');
    }
  }

  editField(field, newValue) {
    if(this.state.original?.labels?.includes('readonly') && field !== 'context' && !this.state.readOnlyChangeConfirmed) {
      if(!confirm('The lego is readonly, are you sure you want to change it?')) {
        return;
      }
      this.setState({ readOnlyChangeConfirmed: true });
    }
    let updated = this.state.changedObject;
    _.set(updated, field, newValue);
    this.setState({ changedObject: updated }, () => this.checkChanges());
  }

  checkChanges() {
    const areEqual = _.isEqual(this.state.changedObject, this.state.original);

    this.setState({ changed: !areEqual });
  }

  discardChanges() {
    this.setState({changed: false, changedObject: _.cloneDeep(this.state.original)})
  }

  async onSave(lego, closeDialog) {
    if(this.state.changed) {
      if(this.state.original?.labels?.includes('readonly')) {
        if(!confirm('The lego is readonly, are you sure you want to change it?')) {
          return;
        }
        this.setState({ readOnlyChangeConfirmed: false });
      }

      if (lego.type === 'fusebox' && lego.state === 'published') {
        console.time('check fusebox context collisions');
        const collisions = await this.checkFuseboxContextCollissions(lego);
        console.timeEnd('check fusebox context collisions');

        if (collisions?.length) {
          this.context.page.alert(<div>
            There are context collisions:

            {collisions.map(c => {
              return <div key={c._id} className={'p-1 border bg-light mb-1'}>
                <LegoEditButton legoId={c._id} icon="preview" editorProps={{ title: `Context collision with ${c._id}` }}>
                  {c._id} <UserAvatarLive user={c.updatedBy} /> <NiceDate t={c.updatedAt} />
                </LegoEditButton>

                <LegoContextSummary context={c.context} limit={20} />

                <PublishedFuseboxThumbnail context={c.context[0]} locale={lego.locales?.[0]} boxIndex={0} />
              </div>;
            })}
          </div>, "Can't save. There are context collisions. Fix them and retry");
          return;
        }
      }

      let res;
      if (this.props.onSave) {
        res = await this.props.onSave(lego, closeDialog);
      } else {
        res = await this.defaultSave(lego, closeDialog)
      }


      // In case this is an existing fusebox template, confirm if dependant fuseboxes should be updated
      if (lego.type === 'fuseboxTemplate' && !lego.__new) {
        if (!_.isEmpty(lego.data[0].templateRenameMap)) {
          if (confirm('The fusebox template has a rename map, do you want to apply that rename map to all the fuseboxes using the template and then clean it?')) {
            res = await this.legoTemplateRenameService.update({ query: { 'templateId': lego._id } }, {});
            // There is no point in not updating any other fusebox... no need to ask
            await this.legoForceUpdateEventService.find({ query: { 'data.templateId': lego._id } });
            return res;
          }
        }

        if (confirm('Update fuseboxes that depend on this template?')) {
          await this.legoForceUpdateEventService.find({ query: { 'data.templateId': lego._id } });
        }
      }

      if (lego.type === 'fusebox' && !_.isEqual(this.state.changedObject.data, this.state.original.data)) {
        let translations = _.filter(lego.labels, l => l.match(/translatedTo:/));

        if (translations.length && confirm(`Fusebox has ${translations.length} translations, do you want to update them?`)) {
          this.dataEditorRef.current?.openTranslateDialog();
        }
      }

      return res;
    } else {
      this.props.onCancel();
    }
  }

  async checkFuseboxContextCollissions({ _id, context, locales }) {
    const cleanContext = contextObj => _.pickBy(contextObj, (v, key) => !key.startsWith('_'));
    const matchingContexts = _.map(context, cleanContext).filter(c => !_.isEmpty(c));
    if (matchingContexts.length) {
      const legos = await this.context.page.service('services/legos').find({
        query: {
          $or: matchingContexts.map(c => ({ context: { $elemMatch: c } })),
          ... (_id ? { _id: { $ne: _id } } : {}),
          type: 'fusebox',
          state: 'published',
          locales: { $in: locales },
          $select: { _id: true, context: true, updatedBy: true, updatedAt: true },
          $limit: 5
        }
      });

      // Filter false positives: cases where the mongo elemMatch implies a subset of matching properties but not all
      legos.forEach(l => l.context = l.context.filter(c => matchingContexts.some(o => _.isEqual(cleanContext(c), o))));
      const realCollisions = legos.filter(l => l.context.length);

      return realCollisions?.length ? realCollisions : false;
    }
  }

  getDataEditor(lego, data) {
    if (lego.type === 'article' ) {
      // Use updatedAt as key to ensure that when a 'save' is made, the article editor gets rebuilt
      // This is useful for when new references have been added
      return <ArticleEditor key={(lego.updatedAt || "new").toString()} onChange={(json) => this.editField('data', json)} value={data}/>;
    } else if (lego.type === 'image') {
      return <div>
        <SingleImageUrlEditor large={true} onChange={(json) => this.editField('data', json)} value={data}/>

        <SingleTextEditor value={lego.data?.semanticVariation}
                          label={'Semantic variation'}
                          placeholder={'None (default)'}
                          className={"mt-1"}
                          small
                          onChange={(text) => this.editField('data.semanticVariation', text)}
        />
      </div>
    } else if (lego.type === 'fuseboxTemplate' || lego.type === 'fusebox') {
      return <FuseboxLegoEditor ref={this.dataEditorRef} isTemplate={lego.type === 'fuseboxTemplate'} lego={lego} openedFusebox={this.props.openedFusebox} onChange={(json) => this.editField('data', json)} value={data} changed={this.state.changed}  onReload={() => this.refreshDbLego()}/>;
    } else if (lego.type === 'text') {
      return <TextFieldEditor rows={20} onChange={(json) => this.editField('data', json)} value={data}/>;
    } else if (lego.type === 'subarticle') {
      return <ArticleMarkupRichTextEditor value={data} onChange={html => this.editField('data', html)}/>;
    } else if (lego.type === 'list') {
      return <JsonListEditor className={'list-editor'} treatValuesAsString={true} valueToDiff={this.state.original?.data} onChange={(json) => this.editField('data', json)} value={data}/>;
    } else if (lego.type === 'symptom' && lego.intentions?.includes('warning-lights')) {
      return <WarningLightsSymptomLegoEditor key={(lego.updatedAt || "new").toString()} onChange={(json) => this.editField('data', json)} value={data} semantics={lego.semantics}/>;
    } else if (lego.type === 'symptom') {
      return <SymptomLegoEditor key={(lego.updatedAt || "new").toString()} onChange={(json) => this.editField('data', json)} value={data}/>;
    } else if (lego.type === 'data' && lego.intentions?.includes('warning-lights-incomplete')) {
      const schema = {
        url: (val, onChange) => <div className={'d-flex'}>
          <SingleTextEditor small className={'zoom-90'} value={val} onChange={onChange}/>
          {val && val.match(/(jpe?g|png)$/i) ? <ImagePreview img50 url={val}/> : null}
        </div>
      };
      return <JsonGUIEditor rows={20} schema={schema} editOnClick={true} onChange={(json) => this.editField('data', json)} value={data}/>;
    } else {
      if(_.isObject(data)) {
        return <JsonGUIEditor rows={20} editOnClick={true} onChange={(json) => this.editField('data', json)}
                               value={data}/>;
      } else {
        return <JsonTextEditor rows={20} className={'monospace'} onChange={(json) => this.editField('data', json)}
                               value={data}/>;
      }
    }
  }

  refreshDbLego() {
    this.context.page.runAsync(async () => {
      const freshLego = await this.context.page.service('services/legos').get(this.state.original._id);
      this.setState({ original: freshLego}, () => this.discardChanges());
    });
    // this.props?.onRefresh();
  }

  expandGenerationsToSameModelId(context) {
    let genByModelId = {};
    for(let c of context) {
      if(c.genId) {
        genByModelId[c.modelId] = c.genId;
      }
    }
    
    this.editField('context', _.map(context, c => {
      if(!c.genId && genByModelId[c.modelId]) {
        return { ...c, genId: genByModelId[c.modelId] };
      } else {
        return c;
      }
    }))
  }

  render() {
    let l = this.state.changedObject;
    let changed = this.state.changed;
    let { _id, type, intentions, semantics, locales, context, generatedContext, data, updatedAt, updatedBy, source, labels, notes, state, createdAt, createdBy, clonedId, __unsaved, __new, __deleted } = l;

    let rowClasses = 'p-2 ';
    if (__new) {
      rowClasses += ' bg-light-success';
    }
    if (__unsaved) {
      rowClasses += ' bg-light-warning';
      console.log(l)
    }
    if (__deleted) {
      rowClasses += ' bg-light-danger';
    }

    if (changed) {
      rowClasses += ' bg-light-warn';
    }

    if (labels?.includes('readonly')) {
      rowClasses += ' bg-light-violeta';
    }

    let twoCols = _.includes(['article'], type);
    let twoEvenCols = _.includes(['image'], type);

    let editTime = '', creationTime = '';
    if (updatedAt && updatedAt !== createdAt) {
      editTime = <span className={'align-middle'}>Edited {dayjs(updatedAt).fromNow()}</span>;
    }
    if (createdAt) {
      creationTime = <span className={'align-middle'}>Created {dayjs(createdAt).fromNow()}</span>;
    }

    let btnCompare = null;
    if (changed) {
      btnCompare = <BtnSeeDiff a={this.state.original} b={this.state.changedObject}/>;
    }

    let readonlyWarning = null;
    if(labels?.includes('readonly') && changed) {
      readonlyWarning = <div className={'alert alert-danger'}>WARNING: This lego is supposed to be <strong>READ ONLY</strong>, you should not change it!</div>
    }

    return <div>
      <LeftRightLayout className={rowClasses}>
        <div className={'h5 mb-0 d-flex align-items-center'}>
            <span className={'ml-2'}>
              <BadgeId id={_id}/>
            </span>

            <span className={'text-secondary zoom-75 small ml-4 mr-2 align-middle'} title={createdAt}>
              {createdBy ? <UserAvatarLive user={createdBy}/> : null} &nbsp;
              {creationTime}
            </span>

            <span className={'text-info zoom-75 ml-2 small mr-2 align-middle'} title={updatedAt}>
              {(updatedBy && editTime) ? <UserAvatarLive user={updatedBy}/> : null} &nbsp;
              {editTime}
            </span>

          <IconButton icon={'code'} level={'primary'} onClick={() => {
            this.modalActionsBus.emit('open', <div>
              <pre className={'small'}>{JSON.stringify(this.state.changedObject, true, 2)}</pre>
            </div>, false);
          }}/>

          {btnCompare}

          { clonedId ? <a target={'_blank'} href={`https://legos.startmycar.com/legos/fusebox?editing="${clonedId}"`} className={'small'}><span className={'badge badge-dark'}>Cloned from: <code>{clonedId}</code></span></a> : null}
        </div>

        <div className='zoom-90'>          
            <LangTranslationSelector legoId={_id} legoType={type} onChange={(lang) => this.editField('lang', lang)} value={l.lang}/>          
            &nbsp;&nbsp;
            <LocalesSelector onChange={(locales) => this.editField('locales', locales)} value={locales}/>
            &nbsp;&nbsp;&nbsp;
            <LegoStateSwitch onChange={(newState) => this.editField('state', newState)} value={state}/>
        </div>
      </LeftRightLayout>

      { readonlyWarning }

      <div className={(twoCols || twoEvenCols) ? 'row m-0' : ''}>
        <div className={twoCols ? 'col-4' : (twoEvenCols ? 'col-6' : '')}>
          <div className={'LegoEditorTable p-2'+(twoCols || twoEvenCols ? ' LegoEditorTable--narrow' : '')}>
            <div className={'LegoEditorTable__label'}>Type:</div>
            <div className={'LegoEditorTable__value'}>
              <TypeSelector value={type} onChange={(type) => this.editField('type', type)}/>
            </div>


            <div className={'LegoEditorTable__label'}>Intentions:</div>
            <div className={'LegoEditorTable__value'}>
              <IntentionsSelector value={intentions} onChange={(intentions) => this.editField('intentions', intentions)}/>
            </div>

            <div className={'LegoEditorTable__label'}>Semantics:</div>
            <div className={'LegoEditorTable__value'}>
              <InputMultipleSemantics value={semantics} onChange={(semantics) => this.editField('semantics', semantics)}/>
            </div>

            <div className={'LegoEditorTable__label'}>
              Context:
              { context?.length > 1 ? <div>
                <IconButton icon={'sort'} level={'primary'} title="Sort by modelId,year desc" onClick={() => this.editField('context', _.sortBy(context, 'modelId', 'year'))}/>

                { context.some(c => c.genId) ? <>
                <br/>
                <IconButton icon={'expand'} level={'primary'} title="Expand generations to same modelId" onClick={() => this.expandGenerationsToSameModelId(context)}/>
                </> : null }
              </div> : null }
            </div>

            <div className={'LegoEditorTable__value LegoEditorTable__value--span5'}>
              {/*<LegoContextSummary context={context}/>*/}
              <MultipleContextsEditor onChange={(json) => this.editField('context', json)} locale={locales?.[0]} value={context}/>
            </div>

            {
              generatedContext?.length
              ?
              <>
                <div className={'LegoEditorTable__label small pt-0 text-secondary'}>Generated:</div>
                <div className={'LegoEditorTable__value LegoEditorTable__value--span5 zoom-75'}>
                  <LegoContextSummary inline context={generatedContext} locales={locales}/>
                </div>
              </> : null
            }

            <div className={'LegoEditorTable__label'}>Source:</div>
            <div className={'LegoEditorTable__value LegoEditorTable__value--span3'}>
              <SourceEditor onChange={(json) => this.editField('source', json)} value={source}/>
            </div>
            <div className={'LegoEditorTable__label'}>Labels:</div>
            <div className={'LegoEditorTable__value small'}>
              <LegoLabelsSelector onChange={(values) => this.editField('labels', values)} value={labels || []}/>
            </div>
          </div>
        </div>

        <div className={'p-2' + (twoCols ? ' col-8' : (twoEvenCols ? ' col-6' : ''))}>
          {this.getDataEditor(l, data)}
        </div>
      </div>

      <div className={'p-2'}>
          <NotesEditor value={notes} onChange={updatedNotes => this.editField('notes', updatedNotes)}/>
      </div>

      <div className={'LegoEditorBottomBar mt-3 '}>
        <div className={rowClasses}>
          <button onClick={() => this.context.page.runAsync(() => this.onSave(l, true))}
                  className={`btn btn-${changed ? 'primary' : 'secondary'} mr-2`}>{__new ? 'Save new lego' : changed ? 'Save and close' : 'Ok'}</button>


          {
            changed
              ?
              <>
                <button onClick={() => this.props.onCancel()} className={'btn btn-default mr-2'}>Cancel</button>

                <button onClick={() => {
                  this.context.page.runAsync(async () => {
                    let updatedLego = await this.onSave(l);
                    // It could fail and return undefined
                    if(updatedLego) {
                      this.setState({ original: updatedLego, changedObject: _.cloneDeep(updatedLego) });
                      this.checkChanges();
                    }
                  })
                }} className={'btn btn-default'}>Save changes</button>
              </>
              :
              null
          }
        </div>
      </div>


      <ModalManager actionsBus={this.modalActionsBus}/>
    </div>;
  }
}

export default LegoEditor;

// export default function LegoEditorComp(props) {
//   return <LegoEditor {...props}/>;
// }